// Itinerary related models

import { IGuest, ITravelRequest, TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { ItineraryActivity } from "./activity.model";
import { ItineraryDay } from "./itinerary-day.model";
import { dateToLocalString } from "@/components/utils/dateUtils";

export type ItineraryStatus = 'draft' | 'pending' | 'approved' | 'completed' | 'rejected' | 'sended' | 'expired';

export type ItineraryTotals = {
  commisionable_total: number;
  not_commisionable_total: number;
  p2b_commission_percentage: number;
  p2b_commission_amount: number;
  td_fee_percentage: number;
  total: number;
}

export interface IItineraryData {
  id?: string;
  title: string;
  start_date: string;
  end_date: string;
  guests_data: IGuest[];
  budget: number;
  description: string;
  travel_request_id?: string;
  image?: string | null;
  itinerary_days?: ItineraryDay[];
  status?: ItineraryStatus;
  note?: string;

  commisionable_total?: number,
  not_commisionable_total?: number,
  p2b_commission_percentage: number,
  p2b_commission_amount?: number,
  td_fee_percentage: number,
  total?: number,
}

export class ItineraryData implements IItineraryData {
  id?: string;
  title: string;
  start_date: string;
  end_date: string;
  guests_data: IGuest[];
  budget: number;
  description: string;
  image: string | null;
  status?: ItineraryStatus;
  travel_request_id?: string;
  travel_request: TravelRequest;
  itinerary_days: ItineraryDay[] = [];
  note: string;

  commisionable_total?: number;
  not_commisionable_total?: number;
  p2b_commission_percentage: number;
  p2b_commission_amount?: number;
  td_fee_percentage: number;
  total?: number;

  get total_guests(): number {
    return this.guests_data.length || 1;
  }

  get firstDay(): string {
    return this.itinerary_days[0]?.date || '';
  }

  get hasTravelData(): boolean {
    return !!(
      this.title &&
      this.start_date &&
      this.end_date &&
      this.total_guests
    );
  };

  constructor(data?: IItineraryData) {
    this.id = data?.id || '';
    this.title = data?.title || '';
    this.image = data?.image || null;
    this.start_date = data?.start_date || '';
    this.end_date = data?.end_date || '';
    this.guests_data = data?.guests_data || [];
    this.budget = data?.budget || 0;
    this.description = data?.description || '';
    this.itinerary_days = data?.itinerary_days?.map(day => new ItineraryDay(day)) || [];
    this.travel_request_id = data?.travel_request_id || '';
    this.status = data?.status || 'draft';
    this.note = data?.note;


    this.commisionable_total = data?.commisionable_total || 0;
    this.not_commisionable_total = data?.not_commisionable_total || 0;
    this.p2b_commission_percentage = data?.p2b_commission_percentage || 22;
    this.p2b_commission_amount = data?.p2b_commission_amount || 0;
    this.td_fee_percentage = data?.td_fee_percentage || 70;
    this.total = data?.total || 0;
  }

  fetchTravelRequest(data: ITravelRequest) {
    this.travel_request = new TravelRequest(data);
    this.travel_request_id = data.id || '';
    this.title = data.destinations?.join(', ') || '';
    this.start_date = data.travel_start_date || '';
    this.end_date = data.travel_end_date || '';
    this.guests_data = data.guests_data as IGuest[];
    this.budget = data.budget_max || 0;
    this.updateItineraryDays(this.start_date, this.end_date);
  }

  update(data: Partial<IItineraryData>): ItineraryData {
    const newData = new ItineraryData({
      ...this as IItineraryData,
      ...data,
    });
    newData.updateTotals(newData.extractTotals());
    newData.travel_request = this.travel_request;
    return newData;
  }

  setItineraryDays(activities: ItineraryActivity[]) {
    this.itinerary_days = generateDaysFromActivities(activities);
    return this.update({ itinerary_days: this.itinerary_days });
  }

  updateItineraryDays(start_date: string, end_date: string) {
    this.itinerary_days = mergeDays(generateDaysFromDates(start_date, end_date), this.itinerary_days);
    return this.update({ itinerary_days: this.itinerary_days });
  }

  setItineraryNote(note: string) {
    return this.update({ note });
  }

  getPayload() {
    const totals = this.extractTotals();
    return {
      title: this.title,
      start_date: this.start_date,
      end_date: this.end_date,
      guests_data: this.guests_data,
      budget: this.budget,
      description: this.description,
      image: this.image,
      travel_request_id: this.travel_request_id,
      status: this.status,
      note: this.note,
      ...totals
    }
  }

  extractAllActivities() {
    // Centralizza filtri e validazione per il salvataggio
    return this.itinerary_days.flatMap(day => day.activities)
      // Filtro attività 'out'
      .filter(activity => activity.inOrOutActivity !== 'out')
      // Valida ogni attività tramite metodo specifico
      .filter(activity => {
        if (typeof activity.validateActivity === 'function') {
          return activity.validateActivity();
        }
        // fallback: base check su id
        return !!activity.id;
      })
      // Rimuovi i campi non necessari per il salvataggio
      .map(activity => {
        // Elimina campi computati/non persistenti
        const { is_multi_day, inOrOutActivity, ...rest } = activity;
        return rest;
      });
  };


  extractTotals(): ItineraryTotals {
    const allActivities = this.extractAllActivities();
    
    // ricalcolare i totali -> p2b_commission_amount e total
    // mantenere fisso p2b_commission_percentage a 22% (o quello fornito)
    // mantenere fisso td_fee_percentage a 70% (o quello fornito)
    const result: ItineraryTotals = {
      commisionable_total: 0,
      not_commisionable_total: 0,
      p2b_commission_amount: 0,
      p2b_commission_percentage: this.p2b_commission_percentage || 22,
      td_fee_percentage: this.td_fee_percentage || 70,
      total: 0,
    }

    allActivities.forEach(activity => {
      if (!activity.include_price || !('cost' in activity)) return;
      if (activity.type === 'flight') {
        result.not_commisionable_total += activity.cost;
      } else {
        result.commisionable_total += activity.cost;
      }
    });

    result.p2b_commission_amount = result.commisionable_total * (result.p2b_commission_percentage / 100);
    result.total = result.commisionable_total + result.not_commisionable_total + result.p2b_commission_amount;

    return result;
  };

  private updateTotals(totals: ItineraryTotals) {
    this.commisionable_total = totals.commisionable_total;
    this.not_commisionable_total = totals.not_commisionable_total;
    this.p2b_commission_amount = totals.p2b_commission_amount;
    this.total = totals.total;
  }
}

function mergeDays(originalDays: ItineraryDay[], newDays: ItineraryDay[]): ItineraryDay[] {
  if (!originalDays.length) return newDays;
  if (!newDays.length) return originalDays;

  return originalDays.map(activity => {
    const newActivity = newDays.find(a => a.date === activity.date);
    return newActivity || activity;
  });

}

function generateDaysFromActivities(activities: ItineraryActivity[]): ItineraryDay[] {
  const { start_date, end_date } = getStartAndEndDateFromActivities(activities);
  return generateDaysFromDates(start_date, end_date, activities);
}

function generateDaysFromDates(start_date: string, end_date: string, activities: ItineraryActivity[] = []): ItineraryDay[] {

  if (!start_date || !end_date) return [];

  const days: ItineraryDay[] = [];
  const start = new Date(start_date);
  const end = new Date(end_date);


  const endPlusOne = new Date(end);
  endPlusOne.setDate(endPlusOne.getDate() + 1);

  const current = new Date(start);

  while (current < endPlusOne) {
    const currentDateString = dateToLocalString(current);

    const activitiesForDay = activities.filter(activity =>
      activity.date === currentDateString
    );

    days.push(new ItineraryDay({
      date: currentDateString,
      activities: activitiesForDay
    }));

    // Move to next day
    current.setDate(current.getDate() + 1);
  }

  return days;
}

function getStartAndEndDateFromActivities(activities: ItineraryActivity[]): { start_date: string, end_date: string } {
  if (!activities.length) {
    return { start_date: '', end_date: '' };
  }

  let start_date = activities[0].date;
  let end_date = activities[0].date;

  for (let i = 1; i < activities.length; i++) {
    const currentDate = activities[i].date;
    if (currentDate < start_date) {
      start_date = currentDate;
    }
    if (currentDate > end_date) {
      end_date = currentDate;
    }
  }

  return { start_date, end_date };
}